import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Send } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LogoMark } from "../components/Logo";
import { toast } from "sonner";

const Hearts = ({ value, onChange, size = 22, testidPrefix = "rating" }) => (
  <div className="flex gap-1.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        type="button"
        key={n}
        onClick={onChange ? () => onChange(n) : undefined}
        data-testid={`${testidPrefix}-heart-${n}`}
        className={onChange ? "transition-transform duration-200 hover:scale-125" : "cursor-default"}
      >
        <Heart size={size} className={n <= value ? "text-terra" : "text-stone2/40"} fill={n <= value ? "#C86D51" : "none"} />
      </button>
    ))}
  </div>
);

export default function Avis() {
  const { user } = useAuth();
  const [avis, setAvis] = useState(null);
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/avis")
      .then((r) => {
        setAvis(r.data);
        const mine = r.data.find((a) => a.user_id === user?.id);
        if (mine) {
          setText((t) => t || mine.text);
          setRating(mine.rating);
        }
      })
      .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/avis", { text, rating });
      toast.success("Merci ! Je vous lirai avec attention — Alexandra.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const mine = avis?.find((a) => a.user_id === user?.id);

  return (
    <div className="relative mx-auto max-w-4xl px-4 py-12 sm:px-8" data-testid="avis-page">
      <img src="/assets/mandala-sun.jpeg" alt="" className="pointer-events-none fixed inset-0 h-full w-full object-cover opacity-[0.07]" />
      <div className="relative">
        <p className="font-cinzel text-xs font-semibold tracking-[0.35em] text-gold">VOS AVIS</p>
        <h1 className="mt-3 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">Racontez-moi votre expérience</h1>

        <div className="mt-8 flex gap-5 rounded-2xl border border-gold/40 bg-white p-7 shadow-boho">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-gold/50 bg-gold-light">
            <LogoMark size={30} />
          </div>
          <p className="font-serif text-base italic leading-relaxed text-ink sm:text-lg">
            « Bonjour, c'est Alexandra. Votre avis compte vraiment : ce qui vous a touché, ce qui vous a surpris,
            ce que vous rêveriez de trouver ici… Je lis chaque message, et j'y réponds personnellement. »
            <span className="mt-2 block font-cinzel text-xs font-semibold not-italic tracking-[0.25em] text-terra">ALEXANDRA · FONDATRICE</span>
          </p>
        </div>

        {/* Formulaire */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-8 rounded-2xl border border-terra/15 bg-white p-7 shadow-boho"
          data-testid="avis-form"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="font-serif text-lg font-medium text-ink">{mine ? "Modifier mon message" : "Votre message"}</p>
            <Hearts value={rating} onChange={setRating} />
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="avis-text-input"
            rows={4}
            maxLength={500}
            placeholder="Votre expérience du jeu, une belle rencontre, une suggestion…"
            className="mt-4 w-full resize-none rounded-xl border border-terra/20 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-terra"
          />
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-stone2">{text.length}/500</span>
            <button
              type="submit"
              disabled={saving || text.trim().length < 10}
              data-testid="avis-submit-button"
              className="flex items-center gap-2 rounded-full bg-terra px-6 py-3 text-sm font-semibold text-cream shadow-boho transition-all duration-300 hover:bg-terra-dark disabled:opacity-50"
            >
              <Send size={15} /> {mine ? "Mettre à jour mon avis" : "Envoyer mon avis"}
            </button>
          </div>
        </motion.form>

        {/* Mur des avis */}
        <div className="mt-12" data-testid="avis-wall">
          <h2 className="font-serif text-2xl font-medium text-ink">Ils ont joué, ils racontent</h2>
          {avis === null ? (
            <p className="mt-8 text-center text-sm text-stone2">Chargement des avis…</p>
          ) : avis.length === 0 ? (
            <p className="mt-8 rounded-2xl border border-dashed border-terra/30 bg-white/60 p-8 text-center font-serif text-lg italic text-stone2">
              Soyez la première plume à écrire sur ce mur.
            </p>
          ) : (
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              {avis.map((a, i) => (
                <motion.div
                  key={a.user_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06, duration: 0.5 }}
                  className="rounded-2xl border border-terra/15 bg-white p-6 shadow-boho"
                  data-testid={`avis-card-${i}`}
                >
                  <div className="flex items-center gap-3">
                    {a.avatar ? (
                      <img src={a.avatar} alt={a.name} className="h-11 w-11 rounded-full border-2 border-gold/40 object-cover" />
                    ) : (
                      <div className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-gold/40 bg-parch font-serif text-lg text-terra">
                        {a.name?.[0]}
                      </div>
                    )}
                    <div>
                      <p className="font-serif text-base font-semibold text-ink">{a.name}</p>
                      <Hearts value={a.rating} size={13} testidPrefix={`avis-${i}`} />
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-clay">{a.text}</p>
                  {a.reply && (
                    <div className="mt-3 rounded-xl border-l-2 border-gold bg-gold-light/50 p-3" data-testid={`avis-reply-${i}`}>
                      <p className="font-cinzel text-[10px] font-semibold tracking-[0.2em] text-terra">RÉPONSE D'ALEXANDRA</p>
                      <p className="mt-1 text-sm italic leading-relaxed text-ink">{a.reply}</p>
                    </div>
                  )}
                  {a.created_at && (
                    <p className="mt-3 text-[11px] text-stone2">
                      {new Date(a.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
